// Copyright © 2021 Move Closer

import { AnyObject, Injectable } from '@movecloser/front-core'

import {
  AggregatesRelated,
  DriverConfig,
  PossibleTypeDriver,
  Related,
  RelatedRecord,
  RelatedType,
  RelatedTypeDriverRegistry,
  ResolvesRelated,
  ResolvesRelatedAsync
} from './contracts'

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
@Injectable()
export abstract class AbstractRelatedService implements AggregatesRelated, ResolvesRelated {
  /**
   * Record of `RelatedTypeDrivers`.
   */
  protected drivers: RelatedTypeDriverRegistry = {}

  /**
   * Record of all related data.
   */
  protected record: RelatedRecord = {}

  constructor (drivers: RelatedTypeDriverRegistry, record?: RelatedRecord) {
    this.drivers = drivers

    if (record) {
      this.record = record
    }
  }

  public describe (related: Related): AnyObject {
    const driver = this.resolveDriver(related.type)
    return driver.describe(`${related.value}`, this.record)
  }

  public resetDrivers (): void {
    for (let i = 0; i < Object.values(this.drivers).length; i++) {
      const driver = Object.keys(this.drivers)[i] as RelatedType
      this.drivers[driver]!.reset()
    }
  }

  public resolve (related: Related, config?: DriverConfig): AnyObject | AnyObject[] {
    if (typeof config === 'undefined') {
      config = {}
    }

    const driver = this.resolveDriver(related.type)
    return driver.resolve(`${related.value}`, this.record, config, this.resolveDriver.bind(this))
  }

  public storeRelated (record: RelatedRecord): void {
    for (const [key, value] of Object.entries(record)) {
      if (key in this.record && typeof this.record[key as RelatedType] !== 'undefined') {
        this.record[key as RelatedType] = { ...this.record[key as RelatedType], ...value }
      } else {
        this.record[key as RelatedType] = value
      }
    }
  }

  public getRelated (): RelatedRecord {
    return this.record
  }

  /**
   * Resolves driver based on the passed-in `RelatedType`.
   *
   * @param type - `RelatedType` associated with the desired driver.
   */
  protected resolveDriver (type: RelatedType): PossibleTypeDriver {
    const found = this.drivers[type]

    if (typeof found === 'undefined') {
      throw new Error(`RelatedService.resolveDriver(): Unsupported related type [${type}] given.`)
    }

    return found
  }
}

/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
@Injectable()
export abstract class AbstractAsyncRelatedService implements AggregatesRelated, ResolvesRelatedAsync {
  /**
   * Record of `RelatedTypeDrivers`.
   */
  protected drivers: RelatedTypeDriverRegistry = {}

  /**
   * Record of all related data.
   */
  protected record: RelatedRecord = {}

  constructor (drivers: RelatedTypeDriverRegistry, record?: RelatedRecord) {
    this.drivers = drivers

    if (record) {
      this.record = record
    }
  }

  public describe (related: Related): Promise<AnyObject> {
    const driver = this.resolveDriver(related.type)

    try {
      const description = driver.describe(`${related.value}`, this.record)
      return Promise.resolve(description)
    } catch (e) {
      return Promise.reject(e)
    }
  }

  public resetDrivers (): void {
    for (let i = 0; i < Object.values(this.drivers).length; i++) {
      const driver = Object.keys(this.drivers)[i] as RelatedType
      this.drivers[driver]!.reset()
    }
  }

  public resolve (related: Related, config?: DriverConfig): Promise<AnyObject | AnyObject[]> {
    if (typeof config === 'undefined') {
      config = {}
    }
    const driver = this.resolveDriver(related.type)

    try {
      const description = driver.resolve(`${related.value}`, this.record, config, this.resolveDriver.bind(this))
      return Promise.resolve(description)
    } catch (e) {
      return Promise.reject(e)
    }
  }

  public resolveSync (related: Related, config?: DriverConfig): AnyObject | null {
    if (typeof config === 'undefined') {
      config = {}
    }
    const driver = this.resolveDriver(related.type)

    try {
      return driver.resolve(`${related.value}`, this.record, config, this.resolveDriver.bind(this))
    } catch (e) {
      console.warn('resolveSync', e.message, e.stack)
      return null
    }
  }

  public storeRelated (record: RelatedRecord): void {
    for (const [key, value] of Object.entries(record)) {
      if (key in this.record && typeof this.record[key as RelatedType] !== 'undefined') {
        this.record[key as RelatedType] = { ...this.record[key as RelatedType], ...value }
      } else {
        this.record[key as RelatedType] = value
      }
    }
  }

  public getRelated (): RelatedRecord {
    return this.record
  }

  /**
   * Resolves driver based on the passed-in `RelatedType`.
   *
   * @param type - `RelatedType` associated with the desired driver.
   */
  protected resolveDriver (type: RelatedType): PossibleTypeDriver {
    const found = this.drivers[type]

    if (typeof found === 'undefined') {
      throw new Error(`RelatedService.resolveDriver(): Unsupported related type [${type}] given.`)
    }

    return found
  }
}
